import React, { memo } from 'react'
import styled, { useTheme } from 'styled-components'
import { Layer, SpaceChildrenItem } from '~/packages/general/variable/spaceTime.types'
import { StyleForLayer } from './styleForLayer.util'
import { getColorMap } from '~/design'
import { formatAsBrNumber, formatExpressionValue } from '~/prix'
import { isSpecific } from './extraLayerContainment.util'
import { formatNumberAsCluster } from '~/components/map/formatNumberAsCluster'
import { isVariableLayer } from './clusterListInput.util'

type CoverageLayer = Layer & {
  __useCoverageChoropleth?: boolean
}

export const layerLegendWidth = 170
const cellHeight = 15
const cellsLength = 4

const Wrapper = styled.div`
  display: flex;
  width: ${layerLegendWidth}px;
  font-size: 0.9rem;
  flex-direction: column;

  .wrapper-h3 {
    margin-bottom: 1rem;

    h3 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: normal;
      margin-bottom: 2px;
    }
  }

  .chart {
    display: flex;
    flex-direction: row;
    margin-top: auto;

    .bar {
      display: flex;
      width: 20px;

      .gradient {
        flex: 1;
        border-radius: 8px;
      }
    }

    .labels {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      width: calc(100% - 15px - 10px);
      padding-left: 10px;

      span {
        width: 100%;
        height: ${cellHeight}px;
        line-height: ${cellHeight}px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin: 0;
      }
    }

    .center {
      justify-content: center;
    }
  }
`

interface LayerLegendProps {
  layer: Layer
  style: StyleForLayer
  width?: string
  height?: string
}

function LayerLegend({
  layer,
  style,
  width = `${layerLegendWidth}px`,
  height = 'auto',
}: LayerLegendProps) {
  const theme = useTheme()

  const hideActions = ['kpiYearlyCumulativeStats']

  if (!isVariableLayer(layer)) {
    return null
  }

  const variable = layer.variable

  const isHeatmap =
    layer.space.childrenType === 'item' &&
    (layer.space as unknown as SpaceChildrenItem).childrenItemView === 'heatmap'
  const isArc = layer.space.childrenType === 'item' && layer.space.childrenItemView === 'arc'
  const isCluster =
    layer.space.childrenType === 'item' && layer.space.childrenItemView === 'cluster'

  let colorMap = style.colorMap
  if (!colorMap || !colorMap.length) {
    const mustForceToCold =
      (layer as CoverageLayer).__useCoverageChoropleth === true && isSpecific(String(variable.categoryKey))
    colorMap = getColorMap(
      mustForceToCold ? 'more' : (variable.betterDensity ?? 'less'),
      isHeatmap ? 'heatmap' : 'choropleth',
    )
  }
  const isPercentageCustom = style.valueType === 'percentageCustom'

  const isPercentage = variable.unitFormat === 'percentage'

  let min = isPercentageCustom
    ? `${formatExpressionValue(style?.min ?? 0, variable, true)}% de cobertura`
    : isPercentage
      ? `${formatExpressionValue(style?.min ?? 0, variable).replace('.', ',')}`
      : formatExpressionValue(style?.min ?? 0, variable)

  let max = isPercentageCustom
    ? `${formatExpressionValue(style?.max ?? 0, variable, true)}% de cobertura`
    : isPercentage
      ? `${formatExpressionValue(style?.max ?? 0, variable).replace('.', ',')}`
      : formatExpressionValue(style?.max ?? 0, variable)

  let totalNumerator = formatAsBrNumber(style.totalNumerator)
  let totalDenominator = formatAsBrNumber(style.totalDenominator)
  let total = formatAsBrNumber(style.total)
  let outsideSelectionTotalNumerator = formatAsBrNumber(style.outsideSelectionTotalNumerator)
  let outsideSelectionTotalDenominator = formatAsBrNumber(style.outsideSelectionTotalDenominator)
  let outsideSelectionTotal = formatAsBrNumber(style.outsideSelectionTotal)

  if (isHeatmap) {
    min = `Menos ${variable.unitPlural}`
    max = `Mais ${variable.unitPlural}`
  } else if (isArc) {
    colorMap = [theme.colors.primary, theme.colors.secondary]
    min = 'Origem'
    max = 'Destino'
  } else if (isCluster && variable.unitFormat !== 'percentage') {
    min = `${formatNumberAsCluster(style.min)} ${variable.unitPlural}`
    max = `${formatNumberAsCluster(style.max)} ${variable.unitPlural}`
  }

  return (
    <Wrapper
      style={{
        width,
        height,
      }}
    >
      {!hideActions.includes(variable.actionDefinitionKey ?? '') && (
        <div className='wrapper-h3'>
          <h3>Total:</h3>
          <h3>
            {variable.unitFormat === 'percentage'
              ? `${totalNumerator} de ${totalDenominator} ${variable.unitPlural} (${total}%)`
              : `${total} ${variable.unitPlural}`}
          </h3>
          <h3>Fora da seleção:</h3>
          <h3>
            {variable.unitFormat === 'percentage'
              ? `${outsideSelectionTotalNumerator} de ${outsideSelectionTotalDenominator} ${variable.unitPlural} (${outsideSelectionTotal}%)`
              : `${outsideSelectionTotal} ${variable.unitPlural}`}
          </h3>
        </div>
      )}
      {/* Gradiente */}
      <div className='chart'>
        <div
          className='bar'
          style={{
            height: `${cellHeight * cellsLength}px`,
          }}
        >
          {style.valueType === 'hidden' ? null : style.max === style.min ? (
            <div
              className='gradient'
              style={{
                background: style.colorMap[style.max === 0 ? 0 : 2],
              }}
            />
          ) : (
            <div
              className='gradient'
              style={{
                background: `linear-gradient(to top, ${colorMap.join(', ')})`,
              }}
            />
          )}
        </div>
        {/* Legenda do gradiente */}
        <div className={style.max === style.min ? 'labels center' : 'labels'}>
          {!(style.valueType === 'hidden') && (
            <>
              <span title={!isHeatmap ? `Máximo de ${max}` : 'Máximo'}>
                {isHeatmap ? `Mais ${variable.unitPlural}` : max}
              </span>
              {style.max !== style.min && (
                <span
                  style={{ marginTop: 'auto' }}
                  title={!isHeatmap ? `Mínimo de ${min}` : 'Mínimo'}
                >
                  {isHeatmap ? `Menos ${variable.unitPlural}` : min}
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

export default memo(LayerLegend)
